import { CookieOptions, PartialWindow } from "@func-core/core/types";
import { generateUuidV4 } from "@func-core/core/utils/uuid";

const ANONYMOUS_ANALYTICS_ID_COOKIE = "ajs_anonymous_id";

export const getUrlLocale = (windowObj: PartialWindow): string => {
  const pathSegments = windowObj.location.pathname
    .split("/")
    .filter((segment) => segment.length > 0);

  return pathSegments[0] || "";
};

export const getBrowserLocale = (windowObj: PartialWindow): string => {
  return windowObj.navigator.language || windowObj.navigator.languages[0] || "";
};

export const isResultUrl = (windowObj: PartialWindow): boolean => {
  const pathSegments = windowObj.location.pathname
    .split("/")
    .filter((segment) => segment.length > 0);

  return pathSegments.length >= 2 && pathSegments[0] === "results";
};

export const getResultUuidFromUrl = (windowObj: PartialWindow): string | null => {
  const pathSegments = windowObj.location.pathname
    .split("/")
    .filter((segment) => segment.length > 0);

  if (pathSegments.length >= 2 && pathSegments[0] === "results") {
    return pathSegments[1];
  }

  return null;
};

export const getUrlCountryParam = (windowObj: PartialWindow): string | null => {
  try {
    if (!windowObj.location.href) return null;
    const url = new URL(windowObj.location.href);

    return url.searchParams.get("country");
  } catch {
    return null;
  }
};

export const isLeadsNewPath = (windowObj: PartialWindow): boolean => {
  const pathSegments = windowObj.location.pathname
    .split("/")
    .filter((segment) => segment.length > 0);

  return pathSegments.length >= 2 && pathSegments[0] === "leads" && pathSegments[1] === "new";
};

export const getLeadsNewCountryParam = (windowObj: PartialWindow): string | null => {
  if (!isLeadsNewPath(windowObj)) return null;

  return getUrlCountryParam(windowObj);
};

export const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = "__test_storage__";
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);

    return true;
  } catch (e) {
    return false;
  }
};

export const isSessionStorageAvailable = (): boolean => {
  try {
    const testKey = "__test_storage__";
    window.sessionStorage.setItem(testKey, testKey);
    window.sessionStorage.removeItem(testKey);

    return true;
  } catch (e) {
    return false;
  }
};

export const getUrlSearchParams = (windowObj: PartialWindow): URLSearchParams | null => {
  try {
    if (!windowObj.location.href) return null;

    return new URL(windowObj.location.href).searchParams;
  } catch {
    return null;
  }
};

export const isAnalyticsAvailable = (windowObj: PartialWindow): boolean => {
  try {
    return !!(windowObj.analytics && typeof windowObj.analytics.identify === "function");
  } catch {
    return false;
  }
};

export const identifyUserInAnalytics = (
  windowObj: PartialWindow,
  userId: string,
  traits: Record<string, unknown> = {}
): void => {
  try {
    if (isAnalyticsAvailable(windowObj) && windowObj.analytics) {
      windowObj.analytics.identify(userId, traits);
    }
  } catch {
    // Silently fail if analytics is not available
  }
};

export const getCookieValue = (windowObj: PartialWindow, name: string): string => {
  try {
    const cookies: string = windowObj.document?.cookie ?? "";
    if (!cookies) return "";

    const match: RegExpMatchArray | null = cookies.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);

    return match ? match[match.length - 1] : "";
  } catch {
    return "";
  }
};

export function setCookieValue(
  windowObj: PartialWindow,
  name: string,
  value: string,
  {
    path = "/",
    maxAge,
    sameSite = "Lax",
    secure = process.env.NODE_ENV !== "development",
  }: CookieOptions = {}
): void {
  if (!windowObj.document) return;

  const parts = [`${name}=${value}`, `Path=${path}`, `SameSite=${sameSite}`];

  if (typeof maxAge === "number") parts.push(`Max-Age=${maxAge}`);
  if (secure) parts.push("Secure");

  windowObj.document.cookie = parts.join("; ");
}

export function getAnonymousId(windowObj: PartialWindow): string {
  if (!windowObj.document) return "";

  return getCookieValue(windowObj, ANONYMOUS_ANALYTICS_ID_COOKIE);
}

export const getOrCreateAnonymousAnalyticsId = (windowObj: PartialWindow): string => {
  const existingId = getCookieValue(windowObj, ANONYMOUS_ANALYTICS_ID_COOKIE);
  if (existingId.trim()) return existingId;

  const uuid = generateUuidV4();

  try {
    setCookieValue(windowObj, ANONYMOUS_ANALYTICS_ID_COOKIE, uuid, {
      maxAge: 60 * 60 * 24 * 365 * 10,
    });
  } catch {
    // Ignore cookie errors
  }

  return uuid;
};
